// Legal content transcribed verbatim from the HomeAligns app's published
// policies (version 2026-10-25), fetched from the live app on 26 Oct 2026.
// Update this file whenever the app's policies change.

export const PRIVACY_POLICY = {
  title: "HomeAligns Privacy Policy",
  updated: "Last updated: 25 October 2026 (version 2026-10-25)",
  sections: [
    {
      heading: "1. Who we are",
      body: [
        "HomeAligns is operated by Dwellsync Technologies Private Limited (CIN U63119MR2026PTC481755), registered office: Elite J 1503, Palava Phase II, LSG Kalyan Thane, Khoni, Thane, Kalyan, Maharashtra – 421204, India. We are the Data Fiduciary for the personal data described here under India's Digital Personal Data Protection Act, 2023.",
        "Privacy and grievance contact: support@homealigns.com. You can also raise a privacy request in the app under Profile → Privacy & data.",
      ],
    },
    {
      heading: "2. Information we collect",
      list: [
        "Account details: name, email, password (stored only as a one-way hash), optional mobile number, city and state, and your role (household, service provider or HomeAligns staff).",
        "Household records you add: properties, rent and expense entries, assets, appliances and vehicles (brand, model, serial number, purchase date and price, service dates), warranties, insurance policies, documents, invoices and photos, and family members you invite.",
        "Service activity: bookings, service requests and quotes, messages with service providers, reviews and payment records.",
        "Marketplace activity: listings, photos, asking prices, enquiries, messages and reports.",
        "Support and assistant: support tickets, screenshots you attach, and conversations with the HomeAligns Assistant.",
        "Service-provider (vendor) data: business profile, services, service cities, team members, verification and KYC details (such as PAN/GSTIN and documents you upload) and subscription records.",
        "Consent and preference records, privacy requests, and security logs (sign-in sessions and administrative activity).",
      ],
      body: [
        "We do not use advertising or third-party analytics SDKs in the app, and we do not collect your precise location.",
      ],
    },
    {
      heading: "3. Why we use it",
      list: [
        "To run your account and household records, show values and depreciation estimates, and send reminders about services, warranties, insurance and renewals.",
        "To connect you with service providers when you book or send a service request, and to run the marketplace.",
        "To provide support, keep the platform secure, prevent misuse and meet legal obligations.",
        "For optional purposes only with your consent: service offers for upcoming maintenance, HomeAligns tips and offers, AI scanning and the AI assistant. You can withdraw any optional consent at any time; core features keep working.",
      ],
      body: ["We do not sell your personal data."],
    },
    {
      heading: "4. Household details, documents and photos",
      body: [
        "Your household records, documents, invoices, warranties and photos are private to your account and to family members you choose to share with (at the permission level you set). Files are stored privately and are only served to signed-in users who are allowed to see them. Service providers cannot browse your assets, properties, documents or household inventory.",
      ],
    },
    {
      heading: "5. When we share information",
      list: [
        "Bookings: when you book a provider, they receive the details needed to do the job (service, date, address and contact details you provide in the booking).",
        "Service requests: only after you review and confirm a request, matching approved and subscribed providers in your city see a short summary — city, the general area you enter, item type, brand/model, approximate age, the problem you describe and your preferred time. Phone numbers or emails typed into the description are hidden. Your name, phone, email, exact address and documents are not shared. If you choose a provider, you decide which contact details (name, phone, address) to share with that provider only.",
        "Maintenance offers (optional): if you allow it, a similar summary for an asset whose service is due may be shown to a few matching providers, clearly marked as an offer opportunity rather than a booking.",
        "Marketplace: your listing (title, photos, price, city and first name) is visible to signed-in users. Contact details are shared only if you choose to share them in an enquiry.",
        "Family sharing: people you invite see your household according to the permission you grant.",
        "Service providers we use to run HomeAligns (see section 6), and authorities when required by law.",
      ],
    },
    {
      heading: "6. Third-party providers and AI",
      list: [
        "Hosting, database and file storage: the HomeAligns app, database and private file storage run on infrastructure provided by Emergent Labs (emergent.sh) and its cloud providers. Data may be processed outside India.",
        "AI processing: when you choose Scan with AI, the photo or document you select is sent to OpenAI models through the Emergent AI gateway to read details for you to review. HomeAligns Assistant messages are sent the same way with help articles and a short summary of your plan and usage (not your name, contact details, documents or household records). OpenAI states that API data is not used to train its models by default and may be retained for up to 30 days for abuse monitoring. We have not yet confirmed the Emergent gateway's own retention period; we will update this policy when confirmed. AI features are optional and need your consent; please don't include sensitive personal details in chats.",
        "Email: transactional emails (password reset codes, account and booking messages) are sent through an email delivery service managed by Emergent.",
        "Payments: payments are processed by Razorpay. HomeAligns currently runs payments in Test Mode — no real payments are processed and we do not store card or bank details.",
        "Sign-in with Google (if you use it): Google confirms your email and name to us.",
      ],
    },
    {
      heading: "7. How long we keep information",
      list: [
        "Account, household records, files, service requests, marketplace data, consents and notifications: while your account is active, then erased when your account is deleted.",
        "Account deletion: your account is deactivated immediately and erased 7 days after your request unless you cancel.",
        "Booking, payment, subscription and invoice records: kept for up to 8 years where accounting and tax laws require it, with your name, contact details and address removed after account deletion.",
        "Support tickets: kept with identifiers removed for 12 months after account deletion (for disputes), then deleted.",
        "Security and processing logs: kept for at least one year as required by the DPDP Rules, with identifiers replaced by a pseudonym after account deletion.",
        "Backups: our hosting provider may keep backups of the database for disaster recovery. Deleted data can remain in those backups until they expire in the normal cycle. If we ever restore from a backup, we re-apply completed deletions.",
      ],
    },
    {
      heading: "8. Security",
      body: [
        "Connections to HomeAligns use HTTPS. Passwords are stored as salted hashes. Sign-in sessions are stored hashed and expire. Access to every record is checked on our servers for your role and ownership — not just hidden in the app. Uploads are checked for allowed file types, private files are only served to authorised users (web links are short-lived), sign-in and other sensitive actions are rate-limited, and administrative actions are logged. We do not currently add application-level encryption to stored records beyond our hosting provider's protections. No system is completely secure; if a breach affects you, we will tell you and the Data Protection Board of India as the law requires.",
      ],
    },
    {
      heading: "9. Your rights and choices",
      list: [
        "Access: download a copy of your data from Profile → Privacy & data.",
        "Correction: edit your profile and records in the app, or raise a correction request.",
        "Deletion: delete your account from Profile → Privacy & data.",
        "Withdraw consent: switch off any optional consent in Privacy & data. Withdrawal stops future optional processing; it does not affect requests you have already sent.",
        "Nomination: you can nominate a person to exercise your rights in case of death or incapacity by raising a privacy request.",
        "Grievances: raise a privacy request in the app or email support@homealigns.com. We aim to respond within 30 days. If you are not satisfied, you may approach the Data Protection Board of India.",
      ],
    },
    {
      heading: "10. Children",
      body: [
        "HomeAligns is for adults aged 18 and above. Do not create an account for a child.",
      ],
    },
    {
      heading: "11. Changes",
      body: [
        "We will show a notice in the app when this policy changes materially. Optional consents are asked again if a change affects them.",
      ],
    },
    {
      heading: "12. Contact",
      body: [
        "Dwellsync Technologies Private Limited, Elite J 1503, Palava Phase II, LSG Kalyan Thane, Khoni, Thane, Kalyan, Maharashtra – 421204, India. Email: support@homealigns.com.",
      ],
    },
  ],
};

export const TERMS = {
  title: "HomeAligns Terms & Conditions",
  updated: "Last updated: 25 October 2026 (version 2026-10-25)",
  sections: [
    {
      heading: "1. About these terms",
      body: [
        "These terms govern your use of HomeAligns, operated by Dwellsync Technologies Private Limited (CIN U63119MR2026PTC481755), Elite J 1503, Palava Phase II, LSG Kalyan Thane, Khoni, Thane, Kalyan, Maharashtra – 421204, India. By creating an account you agree to these terms and acknowledge our Privacy Policy. You must be 18 or older.",
      ],
    },
    {
      heading: "2. What HomeAligns provides",
      body: [
        "HomeAligns helps you record and manage your household — properties, assets, documents, warranties and policies — sends reminders, and connects you with independent service providers and other users through bookings, service requests and a second-hand marketplace. HomeAligns does not employ service providers and is not a party to the service you agree with a provider unless stated.",
      ],
    },
    {
      heading: "3. Your account",
      body: [
        "Keep your login secure and the information you provide accurate. You are responsible for activity on your account and for the records you add, including documents and photos you upload. Do not upload other people's personal data without their permission.",
      ],
    },
    {
      heading: "4. Customers",
      body: [
        "Service requests, bookings and offers are not guaranteed. Providers decide whether to respond or accept. Reminders are reminders only — they are not bookings, and HomeAligns does not share your details with providers unless you send a request or allow service offers. You choose whether to proceed with a provider.",
      ],
    },
    {
      heading: "5. Service providers (vendors)",
      body: [
        "Providers are independent businesses. A provider profile is visible to customers only when HomeAligns has approved it and the provider's subscription is active. Providers must keep their profile, services and service areas accurate, respond honestly, use customer information only for the specific request or job, not contact customers outside the request without their permission, and comply with applicable laws, including tax and consumer law. Subscriptions buy platform access and listed features only: they do not guarantee leads, bookings, revenue or availability. HomeAligns does not deduct commission from bookings. We may suspend opportunity alerts or accounts for misuse, complaints or failure to meet these terms.",
      ],
    },
    {
      heading: "6. Marketplace conduct",
      body: [
        "List only items you own and may sell, describe them honestly (including defects), and do not post prohibited, counterfeit or unsafe items. Meet safely and verify items before paying. HomeAligns is not a party to marketplace sales and does not handle marketplace payments. We may hide listings that break these rules.",
      ],
    },
    {
      heading: "7. Estimated values",
      body: [
        "Depreciation values, AI-estimated market values and other figures in HomeAligns are automatic estimates for your information only. They are not valuations, appraisals or guaranteed sale prices. Actual resale prices may vary based on condition, demand, location and buyer negotiations.",
      ],
    },
    {
      heading: "8. AI features",
      body: [
        "AI scanning and the assistant can make mistakes. Always review AI-filled details before saving, and don't rely on the assistant for legal, financial or safety decisions.",
      ],
    },
    {
      heading: "9. Payments and plans",
      body: [
        "Membership and vendor plan prices and billing periods are shown before you choose a plan. In the current version payments run in Test Mode and no real money is charged. Refunds and cancellations follow our Refund & Cancellation Policy.",
      ],
    },
    {
      heading: "10. Acceptable use",
      body: [
        "Do not misuse HomeAligns, try to access other users' data, scrape or reverse-engineer the service, send spam, or harass anyone. We may suspend accounts that break these terms.",
      ],
    },
    {
      heading: "11. Service availability and liability",
      body: [
        "We work to keep HomeAligns available and accurate but cannot guarantee uninterrupted service. To the extent permitted by law, HomeAligns is not liable for the work of independent providers, marketplace transactions between users, or indirect or consequential losses.",
      ],
    },
    {
      heading: "12. Changes, governing law and contact",
      body: [
        "We may update these terms and will notify you of material changes in the app. These terms are governed by the laws of India, with courts at Thane, Maharashtra having jurisdiction (subject to legal review). Questions: support@homealigns.com.",
      ],
    },
  ],
};

export const REFUND_POLICY = {
  title: "HomeAligns Refund & Cancellation Policy",
  updated: "Last updated: 25 October 2026 (version 2026-10-25)",
  sections: [
    {
      heading: "Cancelling a booking",
      body: [
        "You can cancel a booking free of charge before the provider starts the job, and any amount paid is refunded in full to your original payment method.",
      ],
    },
    {
      heading: "After work has begun",
      body: [
        "Once a job is in progress, cancellations and refunds are handled case-by-case by HomeAligns support based on the work completed.",
      ],
    },
    {
      heading: "Plans",
      body: [
        "Membership and vendor plan payments currently run in Test Mode, so no real money is charged.",
      ],
    },
    {
      heading: "Need help?",
      body: ["Contact support@homealigns.com."],
    },
  ],
};
